import { getImageUrl } from "../services";
import { anyProductPrice, parsePrice, productPrice } from "./productPrice";
import { orderedProductImages } from "./variants";

/**
 * Met un produit de l'API au format attendu par `Article`.
 *
 * Vit ici, et non dans une page : l'accueil et les bons plans affichent la même
 * carte, et deux copies de ce mappage divergeraient au premier ajout de champ.
 */
/**
 * @param {object} p produit brut de l'API
 * @param {{ preferSample?: boolean }} [opts] `preferSample` : contexte Bons Plans —
 *   affiche le prix unitaire (échantillon) plutôt que le prix de gros, même si
 *   le produit a aussi une variante normale (un produit peut être business ET
 *   bon plan à la fois).
 */
export const mapApiProductToArticle = (p, { preferSample = false } = {}) => {
  if (!p) return null;

  // `GET /products` ne charge pas les variantes : la liste ne peut pas les
  // interroger. Elle lit `sale_price`, que l'API remplit depuis la variante la
  // moins chère de chaque type — présent veut dire « ce type existe ».
  const variants = Array.isArray(p.variants) ? p.variants : [];
  const sampleAvailable = variants.length > 0
    ? variants.some((v) => v && v.sample)
    : productPrice(p, "sample") > 0;
  const hasNormal = variants.length > 0
    ? variants.some((v) => v && v.normal)
    : productPrice(p, "normal") > 0;

  // Flux mixte : prix du gros, et prix échantillon seulement pour un produit
  // vendu uniquement en échantillon. `sale_price` du produit porte déjà les
  // frais bancaires et vaut la variante la moins chère du type.
  // En Bons Plans, c'est l'inverse : le prix unitaire prime, gros en repli.
  const price = preferSample && sampleAvailable
    ? productPrice(p, "sample") || anyProductPrice(p)
    : anyProductPrice(p);

  // Galerie du produit d'abord, photos des combinaisons ensuite.
  const images = orderedProductImages(p);

  const imageArray = images
    .map((img) => getImageUrl(img.url || img.path))
    .filter(Boolean);

  // Le minimum d'import ne s'applique qu'à la marchandise importée. Un produit
  // vendu depuis le stock local part à l'unité, quel que soit le `moq` du
  // produit d'origine — et un Bon Plan s'achète toujours à l'unité, même s'il
  // a par ailleurs une variante normale avec MOQ.
  const minPurchase = preferSample && sampleAvailable
    ? 1
    : hasNormal ? Math.max(1, Number(p.moq) || 1) : 1;

  return {
    id: p.id,
    name: p.label || p.reference || p.slug || "Produit",
    description: p.description || "",
    image: imageArray.length > 0 ? imageArray : ["/img/pro/default.png"],
    prix: price,
    sampleAvailable,
    hasNormal,
    minPurchase,
    commission: parsePrice(p.commission),
    isBusinessOffer: p.is_business_offer === true || p.is_business_offer === 1,
    isBonPlan: p.is_bon_plan === true || p.is_bon_plan === 1,
    // Le produit brut voyage avec la carte : la marge business se calcule
    // depuis le barème de transport, que la page charge une seule fois.
    raw: p,
  };
};

/** Liste renvoyée par l'API, quelle que soit la forme de l'enveloppe. */
export const toProductList = (data) =>
  Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

export default mapApiProductToArticle;
